import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Ticket } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { code: "", discount_type: "percent", value: "", active: true, expires_at: "", usage_limit: "" };

export function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ code: c.code, discount_type: c.discount_type, value: c.value,
    active: c.active, expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "", usage_limit: c.usage_limit ?? "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { code: form.code, discount_type: form.discount_type, value: Number(form.value),
      active: form.active, expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit) };
    try {
      if (editing) await api.patch(`/admin/coupons/${editing}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success(editing ? "Coupon updated" : "Coupon created"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const toggle = async (c) => { await api.patch(`/admin/coupons/${c.id}`, { code: c.code, discount_type: c.discount_type, value: c.value, active: !c.active, expires_at: c.expires_at, usage_limit: c.usage_limit }); load(); };
  const del = async (id) => { if (!window.confirm("Delete this coupon?")) return; await api.delete(`/admin/coupons/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="admin-coupons-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl">Coupons</h2>
        <Button onClick={openAdd} className="rounded-none bg-[#D4AF37] text-black" data-testid="admin-add-coupon"><Plus className="h-4 w-4 mr-1" />Create Coupon</Button>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left">{["Code", "Discount", "Active", "Expires", "Used / Limit", "Actions"].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`coupon-row-${c.code}`}>
                <td className="p-3 font-mono font-medium">{c.code}</td>
                <td className="p-3">{c.discount_type === "percent" ? `${c.value}%` : `$${c.value}`}</td>
                <td className="p-3"><Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`coupon-toggle-${c.code}`} /></td>
                <td className="p-3 text-muted-foreground">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                <td className="p-3">{c.used_count || 0} / {c.usage_limit ?? "∞"}</td>
                <td className="p-3"><div className="flex gap-2">
                  <button onClick={() => openEdit(c)} className="hover:text-[#D4AF37]" data-testid={`coupon-edit-${c.code}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(c.id)} className="hover:text-destructive" data-testid={`coupon-del-${c.code}`}><Trash2 className="h-4 w-4" /></button>
                </div></td>
              </tr>
            ))}
            {coupons.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground"><Ticket className="h-8 w-8 mx-auto mb-2" />No coupons yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Coupon" : "Create Coupon"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="coupon-form">
            <Input required placeholder="CODE (e.g. WELCOME10)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-none font-mono" data-testid="cf-code" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                <SelectTrigger className="rounded-none" data-testid="cf-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="percent">Percentage %</SelectItem><SelectItem value="fixed">Fixed $</SelectItem></SelectContent>
              </Select>
              <Input required type="number" step="0.01" min="0" placeholder="Value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="rounded-none" data-testid="cf-value" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase text-muted-foreground">Expires (optional)</label><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="rounded-none mt-1" data-testid="cf-expires" /></div>
              <div><label className="text-xs uppercase text-muted-foreground">Usage limit (optional)</label><Input type="number" min="1" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} className="rounded-none mt-1" data-testid="cf-limit" /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="cf-active" /><span className="text-sm">Active</span></div>
            <Button className="w-full rounded-none h-11 bg-[#D4AF37] text-black font-semibold" data-testid="cf-save">{editing ? "Update" : "Create"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
